/**
 * <s-carousel> — Lightweight scroll-snap carousel.
 *
 * Native horizontal scrolling (swipe, trackpad, keyboard) does the work; this
 * element adds previous/next commands and dot navigation. It pages by however
 * many slides fit in view (one per page when slides are full width, three when
 * three fit), hides dots for pages that don't exist at the current width, and
 * keeps the dots' aria-current in sync. Wraps around at either end.
 *
 * Commands (via commandfor/command on descendant buttons):
 *   --prev — Scroll to the previous page.
 *   --next — Scroll to the next page.
 *
 * Required child markup:
 *   [data-track]  — Scroll container; its element children are the slides.
 *   [data-dot]    — Optional buttons, one per slide, in slide order.
 */
class SCarousel extends HTMLElement {
  #track = null;
  #slides = [];
  #dots = [];
  #index = 0;
  #frame = 0;
  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#sync());
  };

  connectedCallback() {
    this.#track = this.querySelector('[data-track]');
    if (!this.#track) return;
    this.#slides = Array.from(this.#track.children);
    this.#dots = Array.from(this.querySelectorAll('[data-dot]'));

    // Wire the arrows directly instead of relying on Invoker Commands, so they
    // work in every browser and inside editor previews. preventDefault stops a
    // native command dispatch; stopPropagation stops the document polyfill.
    this.querySelectorAll('[command="--prev"], [command="--next"]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.go(this.#index + (button.getAttribute('command') === '--next' ? 1 : -1));
      });
    });

    this.#dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.go(i));
    });

    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll);
    this.#sync();
  }

  disconnectedCallback() {
    this.#track?.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    cancelAnimationFrame(this.#frame);
  }

  #step() {
    const [first, second] = this.#slides;
    if (!first) return 0;
    return second ? second.offsetLeft - first.offsetLeft : first.offsetWidth;
  }

  #perView() {
    const step = this.#step();
    if (!step) return 1;
    return Math.max(1, Math.floor((this.#track.clientWidth + 1) / step + 0.1));
  }

  #pages() {
    return Math.max(1, Math.ceil(this.#slides.length / this.#perView()));
  }

  go(target) {
    if (!this.#slides.length) return;
    const pages = this.#pages();
    const page = ((target % pages) + pages) % pages;
    const slide = this.#slides[page * this.#perView()];
    const max = this.#track.scrollWidth - this.#track.clientWidth;
    const left = Math.min(slide.offsetLeft - this.#slides[0].offsetLeft, max);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
    this.#setIndex(page);
  }

  #sync() {
    const pages = this.#pages();
    this.#dots.forEach((dot, i) => {
      dot.hidden = i >= pages;
    });
    const max = this.#track.scrollWidth - this.#track.clientWidth;
    const scroll = this.#track.scrollLeft;
    const step = this.#step() * this.#perView();
    const page = scroll >= max - 2 ? pages - 1 : step ? Math.round(scroll / step) : 0;
    this.#setIndex(Math.min(page, pages - 1));
  }

  #setIndex(index) {
    if (index < 0) return;
    this.#index = index;
    this.#dots.forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('s-carousel')) customElements.define('s-carousel', SCarousel);
